import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { Home, Search, ShoppingBag, Menu as MenuIcon, X, User, ClipboardList, Grid, MessageCircle, ClipboardCheck, LogOut } from 'lucide-react';
import Logo from './Logo';
import { useCart } from '../lib/cart';
import { useDistributor, useAdmin, useSuperAdmin } from '../lib/auth';
import { api, absUrl, authed } from '../lib/api';
import { formatMoney } from '../lib/utils';

/**
 * Site header with a **live** search dropdown.
 *
 * Behavior (Batch A — Item 2):
 * - As soon as the user types, matching Product×Variant×Flavour rows are
 *   shown in a floating panel below the input.
 * - Rows appear in the exact configured order
 *   (category → product → variant → flavour). No alphabetical sorting.
 * - Each row shows a small thumbnail on the left.
 * - Clicking a row calls `onOpenSearchHit({ product_id, variant_id, flavour_id })`
 *   which the parent Home page uses to open the Choose Flavour popup with that
 *   exact variant preselected and the specific flavour highlighted as hero.
 */
export default function Header({ onOpenSearchHit }) {
    const navigate = useNavigate();
    const location = useLocation();
    const { count } = useCart();
    const { user, signOut: signOutDistributor } = useDistributor();
    const { admin, signOut: signOutAdmin } = useAdmin();
    const { superAdmin, signOut: signOutSuper } = useSuperAdmin();
    // Item 4 — the most-privileged active realm decides the menu contents.
    const role = superAdmin ? 'super_admin' : (admin ? 'admin' : (user ? 'distributor' : null));
    const [menuOpen, setMenuOpen] = useState(false);
    const [searchOpen, setSearchOpen] = useState(false);
    const [q, setQ] = useState('');
    const [waNumber, setWaNumber] = useState('');
    const [index, setIndex] = useState([]);
    const [indexLoaded, setIndexLoaded] = useState(false);
    const dropdownRef = useRef(null);
    const inputRef = useRef(null);

    useEffect(() => {
        (async () => {
            try { const r = await api.get('/catalog/public-settings'); setWaNumber((r.data?.whatsapp || '').replace(/\D/g, '')); } catch { /* ignore */ }
        })();
    }, []);

    // Lazily fetch the search index the first time the user opens the search.
    useEffect(() => {
        if (!searchOpen || indexLoaded) return;
        (async () => {
            try {
                const r = await api.get('/catalog/search-index');
                setIndex(Array.isArray(r.data) ? r.data : []);
            } catch { /* ignore */ }
            setIndexLoaded(true);
        })();
    }, [searchOpen, indexLoaded]);

    const results = useMemo(() => {
        const t = q.trim().toLowerCase();
        if (!t) return [];
        const parts = t.split(/\s+/).filter(Boolean);
        // Match all whitespace-separated tokens across the searchable text.
        return index.filter(row => {
            const hay = `${row.flavour_name} ${row.product_name} ${row.size_label} ${row.category_name}`.toLowerCase();
            return parts.every(p => hay.includes(p));
        }).slice(0, 40); // Cap dropdown length for perf.
    }, [q, index]);

    // Close dropdown when clicking outside the search area.
    useEffect(() => {
        if (!searchOpen) return;
        const onClick = (e) => {
            if (!dropdownRef.current) return;
            if (dropdownRef.current.contains(e.target)) return;
            if (inputRef.current && inputRef.current.contains(e.target)) return;
            setSearchOpen(false);
        };
        document.addEventListener('mousedown', onClick);
        return () => document.removeEventListener('mousedown', onClick);
    }, [searchOpen]);

    const waHref = waNumber ? `https://wa.me/${waNumber}` : null;

    const goHome = (e) => {
        e?.preventDefault?.();
        if (location.pathname === '/') {
            try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch { window.scrollTo(0, 0); }
        } else {
            navigate('/');
            setTimeout(() => { try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch { window.scrollTo(0, 0); } }, 60);
        }
    };

    const handlePickResult = (row) => {
        setSearchOpen(false);
        setQ('');
        // Ensure we're on Home before firing the popup callback.
        const payload = { product_id: row.product_id, variant_id: row.variant_id, flavour_id: row.flavour_id };
        if (location.pathname !== '/') {
            navigate('/', { state: { openSearchHit: payload } });
        } else {
            onOpenSearchHit?.(payload);
        }
    };

    return (
        <>
            <header className="sticky top-0 z-40 bg-sdf-ivory border-b border-sdf-line">
                <div className="max-w-6xl mx-auto flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2.5">
                    <button aria-label="Home" onClick={goHome} className="p-2 rounded-full hover:bg-black/5 shrink-0" data-testid="header-home">
                        <Home size={22} className="text-sdf-text" />
                    </button>
                    <Logo variant="header" className="ml-0.5" />
                    <div className="flex-1" />
                    <button aria-label="Search" onClick={() => setSearchOpen(v => !v)} className="p-2 rounded-full hover:bg-black/5 shrink-0" data-testid="header-search">
                        <Search size={22} className="text-sdf-text" />
                    </button>
                    <button aria-label="Cart" onClick={() => navigate('/cart')} className="p-2 rounded-full hover:bg-black/5 relative shrink-0" data-testid="header-cart">
                        <ShoppingBag size={22} className="text-sdf-text" />
                        {count > 0 && (
                            <span className="absolute -top-0.5 -right-0.5 bg-sdf-red text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">{count}</span>
                        )}
                    </button>
                    <button aria-label="Menu" onClick={() => setMenuOpen(true)} className="p-2 rounded-full hover:bg-black/5 shrink-0" data-testid="header-menu">
                        <MenuIcon size={22} className="text-sdf-text" />
                    </button>
                </div>
                {searchOpen && (
                    <div className="relative border-t border-sdf-line bg-white/70 backdrop-blur px-3 sm:px-4 py-2" ref={inputRef}>
                        <div className="max-w-6xl mx-auto flex items-center gap-2">
                            <Search size={18} className="text-neutral-500" />
                            <input
                                autoFocus value={q}
                                onChange={(e) => setQ(e.target.value)}
                                placeholder="Search products, flavours or size..."
                                className="flex-1 outline-none bg-transparent py-2 text-sm"
                                data-testid="header-search-input"
                            />
                            {q && <button onClick={() => setQ('')} className="text-neutral-500 p-1" data-testid="header-search-clear"><X size={14} /></button>}
                            <button onClick={() => { setSearchOpen(false); setQ(''); }} className="text-neutral-500 p-1"><X size={16} /></button>
                        </div>
                        {q.trim().length > 0 && (
                            <div
                                ref={dropdownRef}
                                className="absolute left-3 right-3 sm:left-4 sm:right-4 top-full mt-1 rounded-xl border border-sdf-line bg-white shadow-2xl max-h-[70vh] overflow-y-auto z-50"
                                data-testid="search-dropdown"
                            >
                                {!indexLoaded ? (
                                    <div className="px-4 py-3 text-xs text-sdf-text-2">Loading…</div>
                                ) : results.length === 0 ? (
                                    <div className="px-4 py-3 text-xs text-sdf-text-2" data-testid="search-empty">No matches for "{q}".</div>
                                ) : (
                                    <ul>
                                        {results.map(row => (
                                            <li key={`${row.product_id}|${row.variant_id}|${row.flavour_id}`}>
                                                <button
                                                    type="button"
                                                    onClick={() => handlePickResult(row)}
                                                    className="w-full flex items-center gap-3 px-3 py-2 hover:bg-sdf-cream/40 focus:bg-sdf-cream/40 outline-none text-left"
                                                    data-testid={`search-hit-${row.flavour_id}-${row.variant_id}`}
                                                >
                                                    <div className="w-9 h-9 rounded-md overflow-hidden bg-neutral-100 flex-shrink-0">
                                                        {row.thumbnail_url ? (
                                                            <img src={absUrl(row.thumbnail_url)} alt="" className="w-full h-full object-cover" />
                                                        ) : (
                                                            <div className="w-full h-full flex items-center justify-center text-[8px] text-neutral-400">{row.flavour_name?.slice(0, 3)}</div>
                                                        )}
                                                    </div>
                                                    <div className="min-w-0 flex-1">
                                                        <div className="text-sm font-bold text-sdf-text line-clamp-1">{row.flavour_name} <span className="text-sdf-text-2 font-medium">– {row.size_label}</span></div>
                                                        <div className="text-[10px] text-sdf-text-2 line-clamp-1">{row.category_name}</div>
                                                    </div>
                                                    <div className="text-sm font-bold text-sdf-red flex-shrink-0">{formatMoney(row.price)}</div>
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </header>

            {menuOpen && (
                <div className="fixed inset-0 z-50 animate-fade-in" onClick={() => setMenuOpen(false)}>
                    <div className="absolute inset-0 bg-black/40" />
                    <div className="absolute right-0 top-0 bottom-0 w-[85%] max-w-md bg-sdf-ivory shadow-2xl animate-slide-in-right flex flex-col" onClick={(e) => e.stopPropagation()}>
                        {/* Header: centered vertical logo + close X */}
                        <div className="relative px-6 pt-6 pb-4 flex items-center justify-center">
                            <img src="/logo.png" alt="Sri Durga Distribution" className="h-16 w-auto" />
                            <button onClick={() => setMenuOpen(false)} className="absolute top-4 right-4 w-9 h-9 border border-red-300 text-sdf-red rounded-md flex items-center justify-center hover:bg-red-50" aria-label="Close" data-testid="menu-close">
                                <X size={18} />
                            </button>
                        </div>
                        <div className="border-t border-sdf-line" />
                        {/* Menu items — Item 4: menu content depends on active realm. */}
                        <nav className="flex-1 px-6 py-8 space-y-4 overflow-y-auto">
                            <DrawerItem to="/account" icon={<User size={22} className="text-sdf-text" />} label="Account" onClose={() => setMenuOpen(false)} tid="menu-account" />
                            <DrawerItem to="/orders" icon={<ClipboardList size={22} className="text-sdf-text" />} label="Your Orders" onClose={() => setMenuOpen(false)} tid="menu-orders" />
                            {role === 'admin' && (
                                <DrawerItem to="/admin/orders" icon={<ClipboardCheck size={22} className="text-sdf-text" />} label="Check Orders" onClose={() => setMenuOpen(false)} tid="menu-check-orders" />
                            )}
                            {role === 'super_admin' && (
                                <DrawerItem to="/secure-admin-dashboard" icon={<Grid size={22} className="text-sdf-text" />} label="Admin Dashboard" onClose={() => setMenuOpen(false)} tid="menu-admin" />
                            )}
                            {role && (
                                <button
                                    onClick={async () => {
                                        try {
                                            if (role === 'super_admin') { await authed('super_admin', { method: 'POST', url: '/superadmin/auth/logout' }).catch(() => {}); signOutSuper(); }
                                            else if (role === 'admin') { await authed('admin', { method: 'POST', url: '/admin/auth/logout' }).catch(() => {}); signOutAdmin(); }
                                            else { signOutDistributor(); }
                                        } finally { setMenuOpen(false); navigate('/login'); }
                                    }}
                                    className="w-full flex items-center gap-4 px-3 py-3 rounded-2xl hover:bg-white transition-colors text-left"
                                    data-testid="menu-logout"
                                >
                                    <div className="w-14 h-14 rounded-full bg-white border border-sdf-line flex items-center justify-center flex-shrink-0"><LogOut size={22} className="text-sdf-text" /></div>
                                    <span className="flex-1 font-bold text-lg text-sdf-text">Log out</span>
                                    <span className="text-sdf-text-2 text-xl">&rsaquo;</span>
                                </button>
                            )}
                        </nav>
                        {/* WhatsApp CTA pinned bottom */}
                        <div className="border-t border-sdf-line p-4 safe-bottom">
                            {waHref ? (
                                <a href={waHref} target="_blank" rel="noreferrer" onClick={() => setMenuOpen(false)} className="flex items-center justify-center gap-2 w-full rounded-full bg-[#25D366] hover:bg-[#20BD5A] text-white font-bold py-4 tracking-wide shadow" data-testid="menu-whatsapp">
                                    <MessageCircle size={18} /> CHAT ON WHATSAPP
                                </a>
                            ) : (
                                <button onClick={() => alert('WhatsApp number will be available soon (Admin → Settings).')} className="flex items-center justify-center gap-2 w-full rounded-full bg-[#25D366] hover:bg-[#20BD5A] text-white font-bold py-4 tracking-wide shadow opacity-90" data-testid="menu-whatsapp">
                                    <MessageCircle size={18} /> CHAT ON WHATSAPP
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

function DrawerItem({ to, icon, label, onClose, tid, external }) {
    const Component = external ? 'a' : Link;
    const linkProps = external ? { href: to, target: '_blank', rel: 'noreferrer' } : { to };
    return (
        <Component {...linkProps} onClick={onClose} data-testid={tid} className="flex items-center gap-4 px-3 py-3 rounded-2xl hover:bg-white transition-colors">
            <div className="w-14 h-14 rounded-full bg-white border border-sdf-line flex items-center justify-center flex-shrink-0">{icon}</div>
            <span className="flex-1 font-bold text-lg text-sdf-text">{label}</span>
            <span className="text-sdf-text-2 text-xl">&rsaquo;</span>
        </Component>
    );
}
